import { Otp } from '@repo/shared/components'
import { useTranslate } from '@repo/shared/hooks'
import { Button, Card, Col, Icon, Typography } from '@repo/ui/components'
import { useRef, useState } from 'react'
import { useRouter } from '@repo/shared/i18n/navigation'
import {
  useRegisterByPhone,
  useRegisterByEmail,
  useResendOtp,
} from '@repo/services'
import { normalizeMobile } from '@repo/ui/lib'
import { OTP_LENGTH } from '@repo/shared/variables/otp.var'
import { useErrorHandler } from '@repo/shared/hooks'
import { toast } from '@repo/ui/components'
import { EAnalyticsEventName, trackEvent } from '@repo/shared/adapters'

export interface IRegisterOtpProps {
  mobileOrEmail: string
  invitationCode: string
  handleBack: () => void
  type: 'email' | 'mobile'
  otp?: number
  redirectingUrl?: string
}

export const RegisterOtp = (props: IRegisterOtpProps) => {
  const {
    mobileOrEmail,
    invitationCode,
    handleBack,
    type,
    otp,
    redirectingUrl,
  } = props
  const { t } = useTranslate()
  const { push } = useRouter()
  const [otpCode, setOtpCode] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const { errorHandler } = useErrorHandler()
  const resendOtpMutation = useResendOtp()

  const registerByPhoneMutation = useRegisterByPhone()
  const registerByEmailMutation = useRegisterByEmail()

  const submitRef = useRef(null)

  const getResendOtpPayload = () => {
    const base =
      type === 'email'
        ? { email: mobileOrEmail }
        : { phone: normalizeMobile(mobileOrEmail) }

    return otp !== undefined ? { ...base, otp } : base
  }

  const handleResend = () => {
    resendOtpMutation.mutate(getResendOtpPayload(), {
      onSuccess: () => {
        toast.success({
          title: t('common_otp_resend'),
        })
      },
      onError: errorHandler,
    })
  }

  const handleOtpComplete = (code: string) => {
    setOtpCode(code)
  }

  const handleSubmit = async (code: string = otpCode) => {
    if (!code || code.length < OTP_LENGTH) return

    setIsLoading(true)

    if (type === 'email') {
      registerByEmailMutation.mutate(
        {
          email: mobileOrEmail,
          code,
          otp: otp,
          referralCode: invitationCode,
        },
        {
          onSuccess: (res) => {
            if (res?.data?.accessToken && res?.data?.refreshToken) {
              trackEvent({
                name: EAnalyticsEventName.Register,
                properties: {
                  user_id_custom: res.data.userID,
                  signup_method: 'email',
                },
              })
              push(
                redirectingUrl
                  ? `${redirectingUrl}?mobileOrEmail=${mobileOrEmail}`
                  : `/auth/kyc?mobileOrEmail=${mobileOrEmail}`,
              )
            }
          },
          onError: (error) => {
            setIsLoading(false)
            errorHandler(error, t)
          },
        },
      )
    } else {
      registerByPhoneMutation.mutate(
        {
          phone: normalizeMobile(mobileOrEmail),
          code,
          otp: otp,
          referralCode: invitationCode,
        },
        {
          onSuccess: (res) => {
            if (res?.data?.accessToken && res?.data?.refreshToken) {
              trackEvent({
                name: EAnalyticsEventName.Register,
                properties: {
                  user_id_custom: res.data.userID,
                  signup_method: 'phone',
                },
              })
              push(
                redirectingUrl
                  ? `${redirectingUrl}?mobileOrEmail=${mobileOrEmail}`
                  : `/auth/quick-kyc?mobileOrEmail=${mobileOrEmail}`,
              )
            }
          },
          onError: (error) => {
            setIsLoading(false)
            errorHandler(error, t)
          },
        },
      )
    }
  }

  return (
    <Card className='p-xl rounded-sm'>
      <Col className='gap-3xl'>
        <Button
          startIcon={<Icon name='ArrowRightLine' />}
          variant='transparent'
          size='xs'
          onClick={handleBack}
          className='gap-2xs'
        >
          {t('common_back')}
        </Button>
        <Typography size='lg' weight='bold'>
          {type === 'email'
            ? t('registerPage_otp_email')
            : t('registerPage_otp_mobile')}
        </Typography>
        <Otp
          mobileOrEmail={mobileOrEmail}
          onResend={handleResend}
          onComplete={handleOtpComplete}
          onEdit={handleBack}
          focusRef={submitRef}
          resendLoading={resendOtpMutation.isPending}
          resendError={resendOtpMutation.isError}
        />
        <Button
          ref={submitRef}
          size='lg'
          className='w-full'
          onClick={() => handleSubmit()}
          disabled={otpCode.length < OTP_LENGTH}
          loading={
            isLoading ||
            (type === 'email'
              ? registerByEmailMutation.isPending
              : registerByPhoneMutation.isPending)
          }
        >
          {t('common_confirm')}
        </Button>
      </Col>
    </Card>
  )
}
